import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import formatDate from 'src/helpers/formatDate';
import { useDebounce } from 'src/hooks/useDebounce';
import { useGetZohoDeals } from 'src/reactQuery/payments/zohoDeals/queries/useGetZohoDeals';
import { fetchZohoDealsCSV } from 'src/services/napi/zohoDeals/fetchZohoDealsData';
import { ZohoDeal } from 'src/services/napi/zohoDeals/interfaces/zohoDeal.interface';
import { MappedZohoDeals } from './interfaces/mappedZohoDeals.interface';

const ZohoDeals = () => {
  const urlLocation = useLocation();
  const history = useHistory();

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  const filterList = ['All', 'Not Paid'];

  const { data: zohoDealsData, isLoading, refetch: refetchSearch } = useGetZohoDeals({
    orderBy: params.orderBy?.toString(),
    orderDirection: params.orderDirection?.toString(),
    page: params.page?.toString(),
    pageSize: params.pageSize?.toString(),
    search: params.search?.toString(),
    status: params.status?.toString()
  });

  useDebounce(() => refetchSearch(), 500, [params.search]);

  const onDownloadCsv = async () => {
    const response = await fetchZohoDealsCSV({ projectId: params.id!.toString() });

    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `deals.csv`);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const onFilterChange = (status: string) => {
    params.status = status;

    history.push({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });
  };

  const mapToTable = (zohoDeals: ZohoDeal[] | undefined) => {
    const data: MappedZohoDeals[] = [];

    if (!zohoDeals || zohoDeals.length === 0) {
      return data;
    }

    zohoDeals.forEach(
      (
        {
          _id,
          contact,
          name,
          nextBillAmount,
          nextBillDate,
          paid,
          pid,
          total,
          totalPaidDifference,
          totalPaidShouldBe,
          upcomingPayment
        },
        index
      ) => {
        const nextBillDateFiltered =
          nextBillDate && nextBillDate !== '-' ? formatDate(nextBillDate) : nextBillDate;
        data.push({
          _id,
          contact,
          key: index,
          name,
          nextBillAmount,
          nextBillDate: nextBillDateFiltered,
          paid,
          pid,
          total,
          totalPaidDifference,
          totalPaidShouldBe,
          upcomingPayment
        });
      }
    );

    return data;
  };

  const filteredData = mapToTable(zohoDealsData?.deals);

  const columns = [
    {
      dataIndex: 'name',
      key: 'deal_name',
      render: (name: string) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: true,
      title: 'Name'
    },
    {
      dataIndex: 'contact',
      key: 'contact.name',
      render: (contact: string) => {
        return <Copy text={contact} />;
      },
      sorter: true,
      title: 'Contact'
    },
    {
      dataIndex: 'pid',
      key: 'user_pid',
      render: (pid: number) => {
        return <Copy text={pid} />;
      },
      sorter: true,
      title: 'Pid'
    },
    {
      dataIndex: 'total',
      key: 'payment_info.total',
      render: (total: number) => {
        return (
          <Copy
            text={total}
            title="Total amount the customer needs to pay over the duration of the contract"
          />
        );
      },
      sorter: true,
      title: 'Total'
    },
    {
      dataIndex: 'totalPaidShouldBe',
      key: 'payment_info.totalPaidShouldBe',
      render: (totalPaidShouldBe: string) => {
        return (
          <Copy
            text={totalPaidShouldBe}
            title="Total amount customer should have paid as of today"
          />
        );
      },
      sorter: true,
      title: 'Should be'
    },
    {
      dataIndex: 'paid',
      key: 'payment_info.paid',
      render: (paid: string) => {
        return <Copy text={paid} title="Total amount customer paid as of today" />;
      },
      sorter: true,
      title: 'Paid'
    },
    {
      dataIndex: 'totalPaidDifference',
      key: 'payment_info.totalPaidDifference',
      render: (totalPaidDifference: string) => {
        return (
          <Copy
            text={totalPaidDifference}
            title="Debt as of today - The difference between what should have been payed - has been paid"
          />
        );
      },
      sorter: true,
      title: 'Difference'
    },
    {
      dataIndex: 'nextBillAmount',
      key: 'payment_info.nextBill.amount',
      render: (nextBillAmount: number) => {
        return (
          <Copy
            text={nextBillAmount}
            title="Amount that customer should have paid normally (not total)"
          />
        );
      },
      sorter: true,
      title: 'Next Payment'
    },
    {
      dataIndex: 'nextBillDate',
      key: 'payment_info.nextBill.date',
      render: (nextBillDate: string | Date) => {
        let date = nextBillDate;

        if (nextBillDate && nextBillDate !== '-') {
          date = new Date(nextBillDate).toDateString();
        }

        return (
          <Copy text={date?.toString()} title="The date when the customer didn't pay in full" />
        );
      },
      sorter: true,
      title: 'Next/Overdue Date'
    },
    {
      dataIndex: 'upcomingPayment',
      key: 'upcomingPayment',
      render: (upcomingPayment: number) => {
        return (
          <Copy text={upcomingPayment} title="Debt (Difference) + Next payment in the future" />
        );
      },
      sorter: true,
      title: 'Upcoming'
    }
  ];

  const actions = [
    {
      onClick: onDownloadCsv,
      title: 'Download CSV',
      type: 'primary'
    }
  ];

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={zohoDealsData?.paging.total} title="Zoho Deals" />
      {/* @ts-ignore */}
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={filteredData}
        dragAndDropUrl="content"
        filterList={[filterList]}
        loading={isLoading}
        onFilterChange={onFilterChange}
        selectedFilter={[params.status || 'All']}
        totalPages={zohoDealsData?.paging.total}
      />
    </div>
  );
};

export default ZohoDeals;
